import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectSearch, setSearchOption } from "../../reducers/searchSlice";
import { getHost } from "../../reducers/utils";
import { useIntl } from "react-intl";
import "./AirportIataSelect.scss";

export default function AirportIataSelect({ stationindex }) {
  const { stations } = useSelector(selectSearch);
  const dispatch = useDispatch();
  const intl = useIntl();

  const selected = stations?.[stationindex];
  const [query, setQuery] = useState(selected?.label || "");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    setQuery(selected?.label || "");
  }, [selected?.value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!query || query.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    const t = setTimeout(() => {
      fetch(`${getHost()}/airports?search=${encodeURIComponent(query)}`, {
        signal: ctrl.signal,
      })
        .then((r) => r.json())
        .then((data) => {
          const q = query.toLowerCase();
          const items = data?.data?.items || [];
          const mapped = items
            .filter((d) => d.properties?.iata_code)
            .map((d) => ({
              value: d.properties.iata_code,
              label: `${d.label} (${d.properties.iata_code}), ${d.properties.country_de || d.properties.country_en || d.properties.country_code || ""}`,
            }))
            .filter((d) => d.label.toLowerCase().includes(q) || d.value.toLowerCase().includes(q))
            .slice(0, 50);
          setOptions(mapped);
          setLoading(false);
        })
        .catch((err) => {
          if (err.name !== "AbortError") setLoading(false);
        });
    }, 200);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query, open]);

  const commit = (opt) => {
    const picked = opt ? { value: opt.value, label: opt.label } : { value: "", label: "" };
    const other = stationindex === 0 ? stations?.[1] || { value: "", label: "" } : stations?.[0] || { value: "", label: "" };
    const dat = stationindex === 0 ? [picked, other] : [other, picked];
    dispatch(setSearchOption({ property: "stations", data: dat }));
    setQuery(picked.label);
    setOpen(false);
    setActiveIdx(-1);
  };

  const clear = () => {
    setQuery("");
    setOptions([]);
    commit(null);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && options[activeIdx]) {
        e.preventDefault();
        commit(options[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="airport-iata-select" ref={wrapperRef}>
      <div className="airport-iata-select__control">
        <input
          type="text"
          className="airport-iata-select__input"
          value={query}
          placeholder={intl.formatMessage({ id: "pleaseSelectStation" })}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIdx(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            className="airport-iata-select__clear"
            onMouseDown={(e) => {
              e.preventDefault();
              clear();
            }}
            aria-label="Leeren"
          >
            ×
          </button>
        )}
      </div>
      {open && (
        <div className="airport-iata-select__menu">
          {loading && <div className="airport-iata-select__hint">Suche...</div>}
          {!loading && query.length < 2 && <div className="airport-iata-select__hint">Mindestens 2 Buchstaben tippen</div>}
          {!loading && query.length >= 2 && options.length === 0 && <div className="airport-iata-select__hint">Keine Treffer</div>}
          {!loading &&
            options.map((opt, i) => (
              <div
                key={opt.value + "-" + i}
                className={`airport-iata-select__option${i === activeIdx ? " is-active" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(opt);
                }}
                onMouseEnter={() => setActiveIdx(i)}
              >
                {opt.label}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
